/**
 * T2 Business — the top-ten supplier bar chart.
 *
 * The users' spreadsheet asks for exactly this and nothing more ("Bar Chart showed TOP 10", sheet
 * "T2 business report", row 35), so the chart has one form and no type switcher (§7).
 *
 * Ranking by the measure on screen happens here — see `ranked`. The response is ordered by
 * quantity whichever measure is selected, and ordering rows already loaded is presentation, not a
 * business answer (§4.3, DECISIONS.md §30).
 *
 * Bars are coloured by material class rather than by rank — a fabric mill and a trim supplier are
 * not comparable volumes, and colouring by class says so without inventing a threshold.
 */

import { useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, LabelList, Pie, PieChart, XAxis, YAxis } from 'recharts';

import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  chartCells,
  chartColor,
  type ChartConfig,
} from '@/components/ui/chart';
import { integer } from '@/components/report/groupedColumns';
import { LOCALE } from '@/lib/locale';

/** Past ten bars the supplier names stop being readable at portal widths. */
export const TOP_N = 10;

/**
 * Axis ticks and bar labels only. Committed quantity runs to nine figures, and "400,000,000" is
 * wider than the gutter Recharts reserves for it — the full figure stays in the tooltip and the
 * table.
 */
const compact = new Intl.NumberFormat(LOCALE, {
  notation: 'compact',
  maximumFractionDigits: 1,
});

/** Roughly what fits under one bar at ten bars across, in the axis's 12px type. */
const TICK_LINE_CHARS = 14;
/** Two lines of name, then an ellipsis — a third line eats the plot. */
const TICK_MAX_LINES = 2;

export interface TopBarRow {
  t2_group?: unknown;
  material_class?: unknown;
  /** Both measures arrive on every row; the page names the one it wants (`field`). */
  [field: string]: unknown;
}

interface Bar10 {
  label: string;
  qty: number;
  materialClass: string;
  /** This bar's share of every supplier in the response, not of the ten drawn. */
  share: number;
}

const CONFIG: ChartConfig = {
  qty: { label: 'Committed' },
};

function asNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function asText(value: unknown): string {
  return typeof value === 'string' && value.trim() !== '' ? value : '—';
}

/** Supplier names carry newlines in the source; the axis wants one run of words. */
function flatten(name: string): string {
  return name.replace(/\s+/g, ' ').trim();
}

/**
 * Greedy word wrap for an axis tick. Words longer than a line get their own line rather than being
 * broken mid-word — a cut supplier code reads as a different supplier. Anything past
 * {@link TICK_MAX_LINES} is elided, and the table still shows the full value.
 */
function wrapTick(name: string): string[] {
  const lines: string[] = [];
  let current = '';

  for (const word of flatten(name).split(' ')) {
    const candidate = current === '' ? word : `${current} ${word}`;
    if (candidate.length <= TICK_LINE_CHARS || current === '') {
      current = candidate;
    } else {
      lines.push(current);
      current = word;
    }
  }
  if (current !== '') {
    lines.push(current);
  }

  if (lines.length > TICK_MAX_LINES) {
    const kept = lines.slice(0, TICK_MAX_LINES);
    const last = kept[TICK_MAX_LINES - 1] ?? '';
    kept[TICK_MAX_LINES - 1] = `${last.slice(0, TICK_LINE_CHARS - 1)}…`;
    return kept;
  }
  // A single word too long to fit is still capped, or it would run under its neighbours.
  return lines.map((line) =>
    line.length > TICK_LINE_CHARS ? `${line.slice(0, TICK_LINE_CHARS - 1)}…` : line,
  );
}

/**
 * The ten biggest by the measure on screen.
 *
 * The response is ordered by quantity whichever measure is selected, so value has to be ranked
 * here: switching measure changes *which* suppliers are the top ten, not only how tall their bars
 * are. Reordering rows already loaded is the one client-side transform the table does too — it
 * settles no business question, and over a capped response this is the top ten of what came back,
 * exactly as it is for quantity.
 */
function ranked(rows: readonly TopBarRow[], field: string): TopBarRow[] {
  return [...rows].sort((a, b) => asNumber(b[field]) - asNumber(a[field]));
}

export function topBars(rows: readonly TopBarRow[], field: string): Bar10[] {
  const total = rows.reduce((sum, row) => sum + asNumber(row[field]), 0);
  return ranked(rows, field)
    .slice(0, TOP_N)
    .map((row) => {
      const qty = asNumber(row[field]);
      const qtyShare = total === 0 ? 0 : qty / total;
      return {
        label: flatten(asText(row.t2_group)),
        qty,
        materialClass: asText(row.material_class),
        share: qtyShare,
      };
    });
}

/** The hovered bar's own share, read off the datum Recharts hands the tooltip. */
function shareOf(item: unknown): number | null {
  if (typeof item !== 'object' || item === null) {
    return null;
  }
  const datum = (item as { payload?: unknown }).payload;
  if (typeof datum !== 'object' || datum === null) {
    return null;
  }
  const share = (datum as { share?: unknown }).share;
  return typeof share === 'number' && Number.isFinite(share) ? share : null;
}

interface TickProps {
  x?: number;
  y?: number;
  payload?: { value?: unknown };
}

/**
 * Horizontal, wrapped ticks instead of the rotated ones: rotated names ran off the bottom of the
 * card and left the reader tracing a diagonal back to its bar. Carries Recharts' own tick class so
 * the shared chart theme paints it (`ChartContainer`'s `CHART_THEME`).
 */
function WrappedTick({ x = 0, y = 0, payload }: TickProps) {
  const lines = wrapTick(String(payload?.value ?? ''));
  return (
    <text
      x={x}
      y={y}
      textAnchor="middle"
      className="recharts-cartesian-axis-tick-value text-[11px]"
    >
      {lines.map((line, index) => (
        <tspan key={index} x={x} dy={index === 0 ? 12 : 12}>
          {line}
        </tspan>
      ))}
    </text>
  );
}

export function TopSuppliersChart({
  rows,
  field,
  measureLabel,
  complete,
}: {
  rows: readonly TopBarRow[];
  /** The row field to plot — quantity or value, chosen by the page's measure control. */
  field: string;
  /** What is being measured, inside a sentence — "ordered quantity", "invoiced value (HKD)". */
  measureLabel: string;
  /**
   * The response was not capped. A share is a whole-result claim (§4.3): over a truncated result
   * the percentage would be of the page rather than of the business, so it is withheld.
   */
  complete: boolean;
}) {
  const bars = topBars(rows, field);

  if (bars.length === 0) {
    return null;
  }

  const title = `Top ${TOP_N} supplier groups by ${measureLabel}`;

  return (
    <section className="flex flex-col gap-1.5" aria-labelledby="t2-top-suppliers">
      <h2 id="t2-top-suppliers" className="text-sm font-medium">
        {title}
      </h2>

      <ChartContainer config={CONFIG} className="aspect-auto h-64 w-full" aria-label={title}>
        <BarChart data={bars} accessibilityLayer margin={{ top: 20, right: 8, bottom: 8, left: 8 }}>
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={false}
            interval={0}
            height={44}
            tick={<WrappedTick />}
          />
          <YAxis
            width={48}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v) => compact.format(Number(v))}
          />
          <ChartTooltip
            content={
              <ChartTooltipContent
                formatter={(value, _name, item) => {
                  const figure = integer.format(Number(value));
                  const share = complete ? shareOf(item) : null;
                  return share === null ? figure : `${figure} (${(share * 100).toFixed(1)}%)`;
                }}
              />
            }
          />
          <Bar dataKey="qty" radius={2}>
            <LabelList
              dataKey="qty"
              position="top"
              offset={6}
              fontSize={11}
              formatter={(v: unknown) => compact.format(Number(v))}
            />
            {chartCells(bars, (bar) => materialToken(bar.materialClass))}
          </Bar>
        </BarChart>
      </ChartContainer>
    </section>
  );
}

/**
 * The same top ten as the bar, against everyone else — the share question the bar cannot answer,
 * because a bar chart of ten suppliers says nothing about the hundred it left out. Both charts
 * use the same material-class colours; supplier identity comes from the labels and rank order.
 */
interface Slice {
  label: string;
  qty: number;
  materialClass: string;
}

const OTHERS = 'Others';

/** One material class keeps one colour in every chart. */
function materialToken(materialClass: string): 'chart-1' | 'chart-2' {
  return materialClass === 'Fabric' ? 'chart-1' : 'chart-2';
}

/** How far a segment fades while another one is being read. */
const DIMMED = 0.25;

/** Segments under this share go unlabelled — below it the name is longer than the arc it names. */
const LABEL_FROM = 0.03;

/** The gap Recharts leaves between segments, and which its own angles are computed net of. */
const PAD_ANGLE = 1;

/** Vertical room one label needs — two lines of it. Two labels closer than this are pushed apart. */
const LABEL_PITCH = 26;

/** How far outside the ring the text columns sit — the run the leader line has to cover. */
const LEADER = 12;

/**
 * Width of one character of the label text, near enough. `text-xs` in the UI stack measures a
 * shade over 6px on a mixed-case name; rounding up is the safe direction, since the number decides
 * how much of a name is kept and an over-estimate clips one character early rather than running
 * the text out of the card.
 */
const LABEL_CHAR = 6.4;

/** As much of a supplier name as fits beside a segment in a narrow column. */
function clip(name: string, limit = 12): string {
  return name.length > limit ? `${name.slice(0, limit - 1)}…` : name;
}

/**
 * How many characters of a name fit between the label column and the edge of the chart.
 *
 * Measured from the ring the browser actually drew rather than assumed from the margin: the donut
 * sits in a quarter-width column whose ring shrinks with the viewport, and a fixed character limit
 * that suits one width runs "COATS THREAD" out of the card at another. The label column starts at
 * `radius + LEADER` from the centre, so what is left on that side is whatever separates it from
 * the plot edge — and the margins are symmetric, so one number serves both sides.
 */
function nameBudget(cx: number, radius: number): number {
  return Math.max(4, Math.floor((cx - radius - LEADER) / LABEL_CHAR));
}

interface Placed {
  /** The supplier, clipped to what fits. */
  name: string;
  /** Its share, on its own line under the name — see {@link placeLabels}. */
  percent: string;
  /** Which side of the ring, and so which way the text runs. */
  right: boolean;
  x: number;
  y: number;
  /** Where on the arc the leader line starts — the segment's own edge, on its own bearing. */
  fromX: number;
  fromY: number;
}

/**
 * Where every label goes, worked out for the ring as a whole.
 *
 * Recharts places each label on its own segment's bearing and has no notion of the label next to
 * it, so a run of small segments stacks its names on top of each other. This recomputes the same
 * bearings — the sector angles are the value shares of what a full turn leaves after one
 * {@link PAD_ANGLE} per segment, which is how Recharts divides them — then does what Recharts does
 * not: parks the text in a column down each side and pushes any two labels closer than
 * {@link LABEL_PITCH} apart, nearest-the-top first.
 *
 * Every call recomputes the whole layout and takes one entry from it, because the label renderer is
 * called once per segment and each call must agree with the others about where the rest went.
 *
 * The share goes on a second line rather than after the name. On one line the percentage spends a
 * quarter of a narrow column's width, and it is the name that gets clipped to pay for it.
 */
function placeLabels(slices: readonly Slice[], cx: number, cy: number, radius: number): Placed[] {
  const total = slices.reduce((sum, slice) => sum + slice.qty, 0);
  const span = 360 - PAD_ANGLE * slices.length;
  const budget = nameBudget(cx, radius);
  const placed: (Placed & { share: number; index: number })[] = [];

  let start = 0;
  slices.forEach((slice, index) => {
    const share = total === 0 ? 0 : slice.qty / total;
    const sweep = share * span;
    // Recharts measures angles anticlockwise from three o'clock; SVG y grows downward.
    const radians = (-(start + sweep / 2) * Math.PI) / 180;
    start += sweep + PAD_ANGLE;
    const right = Math.cos(radians) >= 0;
    placed.push({
      index,
      share,
      right,
      name: clip(slice.label, budget),
      percent: `${String(Math.round(share * 100))}%`,
      // A column each side rather than the segment's own distance: ragged text beside a ring is
      // harder to read than a list, and a fixed x is what makes the push-apart below safe.
      x: cx + (right ? radius + LEADER : -(radius + LEADER)),
      y: cy + (radius + LEADER) * Math.sin(radians),
      fromX: cx + (radius + 2) * Math.cos(radians),
      fromY: cy + (radius + 2) * Math.sin(radians),
    });
  });

  for (const side of [true, false]) {
    const column = placed
      .filter((entry) => entry.right === side && entry.share >= LABEL_FROM)
      .sort((a, b) => a.y - b.y);
    for (let i = 1; i < column.length; i += 1) {
      const above = column[i - 1];
      const entry = column[i];
      if (above !== undefined && entry !== undefined && entry.y - above.y < LABEL_PITCH) {
        entry.y = above.y + LABEL_PITCH;
      }
    }
    // Pushing down can run the last one out of the plot; lift the column back inside if so.
    const last = column[column.length - 1];
    const floor = cy + radius + LABEL_PITCH;
    if (last !== undefined && last.y > floor) {
      const lift = last.y - floor;
      for (const entry of column) {
        entry.y -= lift;
      }
    }
  }

  return placed;
}

interface SliceLabelProps {
  cx?: number;
  cy?: number;
  outerRadius?: number;
  percent?: number;
  index?: number;
}

/** One entry of {@link placeLabels}, for the segment Recharts is asking about. */
function sliceLabel(slices: readonly Slice[]) {
  return function SliceLabel({
    cx = 0,
    cy = 0,
    outerRadius = 0,
    percent = 0,
    index = 0,
  }: SliceLabelProps) {
    if (percent < LABEL_FROM) {
      return null;
    }
    const entry = placeLabels(slices, cx, cy, outerRadius)[index];
    if (entry === undefined) {
      return null;
    }
    // Elbow, not a straight run: pushing labels apart moves the text off its segment's bearing,
    // and a kinked leader keeps the pairing readable where a diagonal would cross its neighbour's.
    const elbowX = entry.fromX + (entry.right ? 8 : -8);
    const endX = entry.x - (entry.right ? 4 : -4);

    return (
      <g>
        <polyline
          points={`${entry.fromX},${entry.fromY} ${elbowX},${entry.y} ${endX},${entry.y}`}
          fill="none"
          strokeWidth={1}
          className="stroke-muted-foreground/60"
        />
        <text
          x={entry.x}
          y={entry.y}
          textAnchor={entry.right ? 'start' : 'end'}
          dominantBaseline="central"
          className="fill-muted-foreground text-xs"
        >
          {/* The leader meets the label between its two lines, so the pairing still reads. */}
          <tspan x={entry.x} dy="-0.35em">
            {entry.name}
          </tspan>
          <tspan x={entry.x} dy="1.2em" className="fill-muted-foreground/80">
            {entry.percent}
          </tspan>
        </text>
      </g>
    );
  };
}

export function topSlices(rows: readonly TopBarRow[], field: string): Slice[] {
  const order = ranked(rows, field);
  const top = order.slice(0, TOP_N).map((row) => ({
    label: flatten(asText(row.t2_group)),
    qty: asNumber(row[field]),
    materialClass: asText(row.material_class),
  }));
  const rest = order.slice(TOP_N).reduce((sum, row) => sum + asNumber(row[field]), 0);
  return rest > 0 ? [...top, { label: OTHERS, qty: rest, materialClass: OTHERS }] : top;
}

export function TopSuppliersDonut({
  rows,
  field,
  measureLabel,
  complete,
}: {
  rows: readonly TopBarRow[];
  /** The row field to divide up — quantity or value, chosen by the page's measure control. */
  field: string;
  measureLabel: string;
  /**
   * The response was not capped. "Everyone else" is a whole-result claim (§4.3): over a truncated
   * result the Others slice would be the rest of the *page*, presented as the rest of the business.
   */
  complete: boolean;
}) {
  // Which segment the pointer is on. Presentation state, and the only state this file holds.
  const [hovered, setHovered] = useState<number | null>(null);
  const slices = topSlices(rows, field);

  if (!complete || slices.length === 0) {
    return null;
  }

  const total = slices.reduce((sum, slice) => sum + slice.qty, 0);
  const active = hovered === null ? undefined : slices[hovered];
  const title = `Share of ${measureLabel}, top ${TOP_N} supplier groups against the rest`;
  const config: ChartConfig = { qty: { label: measureLabel } };

  return (
    <section className="flex flex-col gap-1.5" aria-labelledby="t2-share">
      <h2 id="t2-share" className="text-sm font-medium">
        {`Share of ${measureLabel}`}
      </h2>

      <div className="relative">
        <ChartContainer config={config} className="aspect-auto h-64 w-full" aria-label={title}>
          {/*
            The margin is the label gutter: the names are drawn outside the ring. Widening it
            trades ring for text and the trade is favourable — the ring shrinks by half of what the
            gutter gains, because the radius follows the smaller of the two plot dimensions.
          */}
          <PieChart margin={{ top: 8, right: 68, bottom: 8, left: 68 }}>
            <Pie
              data={slices}
              dataKey="qty"
              nameKey="label"
              innerRadius="52%"
              outerRadius="78%"
              paddingAngle={1}
              labelLine={false}
              label={sliceLabel(slices)}
              // Off, not left to the default: the labels are drawn only once the sectors finish
              // animating, so an animated ring shows a second of unlabelled colour on every tab
              // switch. Nothing here moves, so nothing is lost by not animating it (§4.9).
              isAnimationActive={false}
              onMouseEnter={(_datum, index: number) => {
                setHovered(index);
              }}
              onMouseLeave={() => {
                setHovered(null);
              }}
            >
              {slices.map((slice, index) => (
                <Cell
                  key={slice.label}
                  fill={chartColor(
                    slice.label === OTHERS
                      ? 'muted-foreground'
                      : materialToken(slice.materialClass),
                  )}
                  fillOpacity={
                    hovered !== null && hovered !== index
                      ? DIMMED
                      : slice.label === OTHERS
                        ? 0.45
                        : 1
                  }
                  stroke={chartColor('card')}
                />
              ))}
            </Pie>
          </PieChart>
        </ChartContainer>

        {/*
          The reading goes in the hole rather than in a tooltip that covers the ring it describes.
          Pointer events off, or the readout would take the hover it exists to report.
        */}
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-0.5 px-16 text-center">
          <span className="max-w-full truncate text-xs text-muted-foreground">
            {active === undefined ? 'Total' : active.label}
          </span>
          <span
            className="text-sm font-medium tabular-nums"
            title={integer.format(Math.round(active === undefined ? total : active.qty))}
          >
            {compact.format(active === undefined ? total : active.qty)}
          </span>
          {active === undefined || total === 0 ? null : (
            <span className="text-xs tabular-nums text-muted-foreground">
              {`${((active.qty / total) * 100).toFixed(1)}%`}
            </span>
          )}
        </div>
      </div>
    </section>
  );
}
